/**
 * Utility functions for CSV file handling
 */

/**
 * Converts an array of objects to a CSV string
 * @param data Array of objects to convert
 * @param headers Optional array of headers to use (if not provided, will use object keys)
 * @returns CSV string
 */
export const objectsToCsv = (data: any[], headers?: string[]): string => {
  if (data.length === 0) return '';
  
  // Use provided headers or extract from first object
  const csvHeaders = headers || Object.keys(data[0]);
  
  // Create header row
  const headerRow = csvHeaders.join(',');
  
  // Create data rows
  const rows = data.map(obj => {
    return csvHeaders.map(header => {
      const value = obj[header];
      
      // Handle different value types
      if (value === null || value === undefined) {
        return '';
      } else if (typeof value === 'string') {
        // Escape quotes and wrap in quotes if contains comma or quotes
        const escaped = value.replace(/"/g, '""');
        return (escaped.includes(',') || escaped.includes('"')) 
          ? `"${escaped}"` 
          : escaped;
      } else if (typeof value === 'boolean') {
        return value ? 'true' : 'false';
      } else {
        return String(value);
      }
    }).join(',');
  });
  
  // Combine header and data rows
  return [headerRow, ...rows].join('\n');
};

/**
 * Parses a CSV string into an array of objects
 * @param csvText CSV string to parse
 * @param headers Optional array of headers to use (if not provided, will use first row)
 * @returns Array of objects
 */
export const csvToObjects = (csvText: string, headers?: string[]): any[] => {
  const lines = csvText.split('\n');
  if (lines.length === 0) return [];

  // Remove BOM if present
  let firstLine = lines[0];
  if (firstLine.charCodeAt(0) === 0xFEFF) {
    firstLine = firstLine.slice(1);
    lines[0] = firstLine;
  }
  
  // Also check for Excel-style BOM in UTF-8
  if (firstLine.startsWith('\ufeff')) {
    firstLine = firstLine.substring(1);
    lines[0] = firstLine;
  }
  
  // Use provided headers or extract from first row
  const csvHeaders = headers || parseCSVLine(lines[0]);
  
  // Parse data rows
  const dataRows = headers ? lines : lines.slice(1);
  
  return dataRows
    .filter(line => line.trim() !== '') // Skip empty lines
    .map(line => {
      const values = parseCSVLine(line.trim());
      const obj: any = {};
      
      // Map values to headers
      csvHeaders.forEach((header, index) => {
        if (index < values.length) {
          const value = values[index];
          
          // Try to convert to appropriate type
          if (value === '') {
            obj[header] = null;
          } else if (value.toLowerCase() === 'true') {
            obj[header] = true;
          } else if (value.toLowerCase() === 'false') {
            obj[header] = false;
          } else if (!isNaN(Number(value)) && value.trim() !== '') {
            obj[header] = Number(value);
          } else {
            obj[header] = value;
          }
        } else {
          obj[header] = null;
        }
      });
      
      return obj;
    });
};

/**
 * Parses a CSV line into an array of values, handling quoted fields
 * @param line CSV line to parse
 * @returns Array of values
 */
export const parseCSVLine = (line: string): string[] => {
  const result: string[] = [];
  let currentField = '';
  let inQuotes = false;
  
  // Handle empty line
  if (!line || line.trim() === '') {
    return [];
  }
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      // If we see a quote followed by another quote, it's an escaped quote
      if (i < line.length - 1 && line[i + 1] === '"') {
        currentField += '"';
        i++; // Skip the next quote
      } else {
        // Otherwise, toggle the inQuotes flag
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // End of field
      result.push(currentField);
      currentField = '';
    } else {
      // Add character to current field
      currentField += char;
    }
  }
  
  // Add the last field
  result.push(currentField);
  
  return result;
};

/**
 * Downloads a CSV file
 * @param csvContent CSV content
 * @param filename Filename to use
 */
export const downloadCsv = (csvContent: string, filename: string): void => {
  // Add BOM for Excel compatibility
  const BOM = '\uFEFF';
  const csvContentWithBOM = BOM + csvContent; // Use this for the blob
  
  const blob = new Blob([csvContentWithBOM], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};